<template>
  <q-select
    v-model="suppliersComputed"
    name="supplier"
    clearable
    use-input
    multiple
    outlined
    dense
    input-debounce="0"
    class="supplier-select-useinput"
    :options="options"
    v-bind="suppliersComputed.length === 0 && { placeholder: '選択してください（複数選択可）' }"
    @clear="clear"
    @filter="filterFn"
  >
    <template #selected>
      <div v-for="(item, index) in suppliersComputed" :key="index">
        <q-chip
          removable
          color="secondary"
          text-color="dark"
          class="q-my-xs q-ml-xs q-mr-none"
          @remove="suppliersComputed.splice(index, 1)"
        >
          <p class="label">{{ item.label }}</p>
        </q-chip>
      </div>
    </template>
    <template #no-option>
      <q-item>
        <q-item-section class="text-grey"> 該当するサプライヤはありません </q-item-section>
      </q-item>
    </template>
  </q-select>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import ResultStatusCode from '../../common/code/ResultStatusCode';
import http from '../../common/axios-common';
import HttpResponseStatusCode from '../../common/code/HttpResponseStatusCode';

interface SupPullDown {
  label: string;
  value: string;
}

interface Props {
  // サプライヤコード
  suppliers: SupPullDown[];
}

const props = defineProps<Props>();

const emit = defineEmits(['clear', 'change']);

const router = useRouter();

// サプライヤ
const suppliersComputed = computed({
  get: () => props.suppliers,
  set: (value) => {
    emit('change', value);
  },
});

// クリア
const clear = () => {
  emit('clear', []);
};

// サプライヤプルダウンリスト
const stringOptions = ref<SupPullDown[]>([]);

// サプライヤ（フィルター用）
const options = ref(stringOptions.value);

// サプライヤ取得
const getSuppliers = () => {
  http
    .post('/suppliers')
    .then((response) => {
      if (response.data.common.status === ResultStatusCode.ERROR) {
        // システムエラー
        router.push({ name: 'error' });
      }
      response.data.supplierSelectItems.forEach(
        (supplier: { supplier_name: string; supplier_code: string }) => {
          stringOptions.value.push({
            label: `${supplier.supplier_code}: ${supplier.supplier_name}`,
            value: supplier.supplier_code,
          });
        }
      );
    })
    .catch((err) => {
      if (err.response.status === HttpResponseStatusCode.SESSIONTIMEOUT) {
        // セッションタイムアウトの場合
        router.push({ name: 'login', query: { timeout: 'timeout' } });
      } else {
        // その他エラーの場合
        router.push({ name: 'error' });
      }
    });
};

// プルダウンフィルター
const filterFn = (val: string, update: (arg0: () => void) => void) => {
  update(() => {
    if (val === '') {
      options.value = stringOptions.value;
    } else {
      const needle = val.toLowerCase();
      options.value = stringOptions.value.filter(
        (v: { label: string; value: string }) =>
          v.label.toLowerCase().indexOf(needle) > -1 || v.value.toLowerCase().indexOf(needle) > -1
      );
    }
  });
};

// 初期表示処理
getSuppliers();
</script>
<style scoped>
p {
  margin: 0;
}

p.label {
  max-width: 320px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
<style>
.supplier-select-useinput .q-field__control {
  align-items: center;
}

.supplier-select-useinput .q-chip {
  min-width: 170px;
  height: auto;
  padding: 4px 14px;
  border-radius: 10px;
}
</style>
